import React, {useState} from "react";
import {Helmet} from "react-helmet-async";
import {Link, useSearchParams} from "react-router-dom";
import {NewsletterService} from "../service/NewsletterService";

export default function UnsubscribePage(): JSX.Element {
    const [searchParams] = useSearchParams();
    // URLSearchParams decodes '+' as space; recover '+' for emails like "user+tag@gmail.com"
    const email = (searchParams.get("email") || "").replace(/ /g, "+");
    const type = searchParams.get("type") || "all";
    const followUpName = searchParams.get("followUpName") || undefined;

    const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState<string>("");

    async function handleUnsubscribe() {
        setStatus("submitting");
        setErrorMessage("");
        try {
            await NewsletterService.cancelNewsletter({
                email,
                type,
                followUpName,
            });
            setStatus("success");
        } catch (err: any) {
            setStatus("error");
            setErrorMessage(err?.message ?? "Something went wrong. Please try again.");
        }
    }

    return (
        <>
            <Helmet>
                <title>Unsubscribe — Effective Action System</title>
                <meta name="robots" content="noindex"/>
            </Helmet>
            <main style={{
                minHeight: "100vh", background: "#0b1410", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "2rem", textAlign: "center",
            }}>
                <div style={{maxWidth: 560}}>
                    {!email && (
                        <>
                            <h1 style={{fontSize: "1.75rem", margin: "0 0 1rem", fontWeight: 700}}>
                                Missing email
                            </h1>
                            <p style={{color: "rgba(255,255,255,0.7)", marginBottom: "2rem"}}>
                                This unsubscribe link is missing the email address. Please use the link from
                                one of our emails, or contact us at{" "}
                                <a href="mailto:support@effectiveactionsystem.com"
                                   style={{color: "#2ad167"}}>support@effectiveactionsystem.com</a>.
                            </p>
                        </>
                    )}

                    {email && status === "success" && (
                        <>
                            <div style={{
                                width: 72, height: 72, margin: "0 auto 1.5rem",
                                borderRadius: "50%", background: "rgba(42,209,103,0.15)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                border: "2px solid #2ad167", fontSize: "2rem", color: "#2ad167",
                            }}>✓</div>
                            <h1 style={{fontSize: "1.75rem", margin: "0 0 1rem", fontWeight: 700}}>
                                Unsubscribed
                            </h1>
                            <p style={{color: "rgba(255,255,255,0.7)", marginBottom: "2rem"}}>
                                <strong>{email}</strong> has been removed from our list.
                                You will no longer receive emails from us.
                            </p>
                        </>
                    )}

                    {email && status !== "success" && (
                        <>
                            <h1 style={{fontSize: "1.75rem", margin: "0 0 1rem", fontWeight: 700}}>
                                Unsubscribe
                            </h1>
                            <p style={{color: "rgba(255,255,255,0.7)", marginBottom: "2rem"}}>
                                Click below to unsubscribe <strong>{email}</strong> from all our emails.
                            </p>
                            <button
                                onClick={handleUnsubscribe}
                                disabled={status === "submitting"}
                                style={{
                                    display: "inline-block", padding: "0.75rem 1.5rem",
                                    background: "#2ad167", color: "#0b1410", border: "none",
                                    borderRadius: 10, fontWeight: 700, fontSize: "1rem",
                                    cursor: status === "submitting" ? "not-allowed" : "pointer",
                                    opacity: status === "submitting" ? 0.6 : 1,
                                }}
                            >
                                {status === "submitting" ? "Unsubscribing..." : "Unsubscribe"}
                            </button>
                            {status === "error" && (
                                <p style={{color: "#ef4444", marginTop: "1.5rem", fontSize: "0.9rem"}}>
                                    {errorMessage}
                                </p>
                            )}
                        </>
                    )}

                    <div style={{marginTop: "2rem"}}>
                        <Link to="/" style={{color: "#2ad167", textDecoration: "underline"}}>
                            Back to home
                        </Link>
                    </div>
                </div>
            </main>
        </>
    );
}
